import React from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
// NOTE: Use the editor from source (not a build)!
import ClassicEditor from "@ckeditor/ckeditor5-editor-classic/src/classiceditor";
import Essentials from "@ckeditor/ckeditor5-essentials/src/essentials";
import Bold from "@ckeditor/ckeditor5-basic-styles/src/bold";
import Italic from "@ckeditor/ckeditor5-basic-styles/src/italic";
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph";
import SourceEditing from "@ckeditor/ckeditor5-source-editing/src/sourceediting";
import Heading from "@ckeditor/ckeditor5-heading/src/heading";
import Link from "@ckeditor/ckeditor5-link/src/link";
import List from "@ckeditor/ckeditor5-list/src/list";
import BlockQuote from "@ckeditor/ckeditor5-block-quote/src/blockquote";
import Table from "@ckeditor/ckeditor5-table/src/table";
import TableToolbar from "@ckeditor/ckeditor5-table/src/tabletoolbar";
import GeneralHtmlSupport from "@ckeditor/ckeditor5-html-support/src/generalhtmlsupport";
import Alignment from "@ckeditor/ckeditor5-alignment/src/alignment";
import PasteFromOffice from "@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice";

import Image from "@ckeditor/ckeditor5-image/src/image";
import ImageUpload from "@ckeditor/ckeditor5-image/src/imageupload";
import {
  ImageResize,
  ImageStyle,
  ImageToolbar,
} from "@ckeditor/ckeditor5-image";
import LinkImage from "@ckeditor/ckeditor5-link/src/linkimage";
import Font from "@ckeditor/ckeditor5-font/src/font";

const editorConfiguration = {
  plugins: [
    Essentials,
    Bold,
    Italic,
    Alignment,
    Paragraph,
    SourceEditing,
    Heading,
    Link,
    List,
    BlockQuote,
    Table,
    TableToolbar,
    GeneralHtmlSupport,
    PasteFromOffice,
    Image,
    ImageUpload,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    LinkImage,
    Font,
  ],

  extraPlugins: [uploadPlugin],

  heading: {
    options: [
      { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
      {
        model: "heading3",
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3",
      },
    ],
  },

  // , CloudServices, EasyImage, Image, ImageCaption, ImageStyle, ImageToolbar, ImageUpload
  //toolbar: ["sourceEditing", "heading", "bold", "italic"]

  //'resizeImage:25', 'resizeImage:50', 'resizeImage:75',
  image: {
    styles: ["full", "alignLeft", "alignRight"],
    toolbar: [
      "imageStyle:alignLeft",
      "imageStyle:alignCenter",
      "imageStyle:alignRight",
      "|",
      "resizeImage:25",
      "resizeImage:50",
      "resizeImage:75",
      "resizeImage:original",
      "resizeImage",
      "|",
      "imageTextAlternative",
      "linkImage",
    ],
    resizeUnit: "%",
  },
  fontFamily: {
    options: [
      "default",
      "Inter,sans-serif",
      "Arial, Helvetica, sans-serif",
      "Courier New, Courier, monospace",
      "Times New Roman, Times, serif",
      "Trebuchet MS, Helvetica, sans-serif",
    ],
  },
  toolbar: {
    items: [
      "sourceEditing",
      "|",
      "heading",
      "|",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "bold",
      "italic",
      "alignment",
      "|",
      "link",
      "bulletedList",
      "numberedList",
      "|",
      "imageUpload",
      "insertTable",
      "|",
      "blockQuote",
      "undo",
      "redo",
    ],
  },

  htmlSupport: {
    allow: [
      {
        //name: /.*/,
        name: /^(p|h[1-6]|div|ul|ol|li|table|tbody|thead|tr|th|td|blockquote|figure|figcaption|img|a)$/,
        attributes: true,
        classes: true,
        styles: true,
      },
    ],
    disallow: [],
  },
  clipboard: {
    matchers: [],
  },
  removePlugins: ["CKBox", "CKFinder"],
  // 'uploadImage',
};

function uploadAdapter(loader) {
  return {
    upload: () => {
      return new Promise((resolve, reject) => {
        const body = new FormData();
        loader.file.then((file) => {
          body.append("files", file);
          fetch(`${process.env.REACT_APP_HOST}/api/admin/user/uploadImage`, {
            method: "post",
            body: body,
          })
            .then((res) => res.json())
            .then((res) => {
              if (res.code === 200 && res.data && res.data.image) {
                resolve({
                  default: res.data.image,
                });
              } else {
                reject(res?.message || "Image upload failed");
              }
            })
            .catch((err) => {
              reject(err);
            });
        });
      });
    },
  };
}

function uploadPlugin(editor) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
    return uploadAdapter(loader);
  };
}

const CustomEditor = (props) => {
  const editorRef = React.useRef(null);
  const loadedDataRef = React.useRef(null);
  // CKEditor's own setData() (used to hydrate the editor from external
  // `props.data`, both on initial ready and whenever that prop changes)
  // can itself trigger the editor's change event - and CKEditor often
  // re-serializes/normalizes the HTML slightly in the process, so the
  // resulting getData() can differ from the exact string that was set
  // even though the user never touched anything. This flag suppresses
  // forwarding onChange to the parent for those programmatic hydration
  // events - only a REAL user edit should ever call props.onChange.
  const isHydratingRef = React.useRef(false);

  // Hydrate editor when external data arrives or changes
  React.useEffect(() => {
    const dataToSet = props.data || "";
    if (editorRef.current && dataToSet !== loadedDataRef.current) {
      isHydratingRef.current = true;
      editorRef.current.setData(dataToSet);
      loadedDataRef.current = editorRef.current.getData();
      isHydratingRef.current = false;
      if (props.onDataLoaded) {
        props.onDataLoaded(loadedDataRef.current);
      }
    }
  }, [props.data]);

  return (
    <CKEditor
      editor={ClassicEditor}
      config={editorConfiguration}
      className={props.className}
      onReady={(editor) => {
        editorRef.current = editor;
        const dataToSet = props.data || "";
        isHydratingRef.current = true;
        editor.setData(dataToSet);
        loadedDataRef.current = editor.getData();
        isHydratingRef.current = false;
        // Tell the parent what CKEditor itself considers this content to
        // be (post-normalization), so it can be used as the "unchanged"
        // baseline for change-detection instead of the raw stored string
        // - CKEditor can settle/re-normalize certain markup (e.g. this
        // blog's many empty <figure>&nbsp;</figure> placeholders) again
        // on later focus/blur, which would otherwise make getData() keep
        // drifting away from the original DB value even without a real
        // edit.
        if (props.onDataLoaded) {
          props.onDataLoaded(loadedDataRef.current);
        }
      }}
      onChange={(event, editor) => {
        const data = editor.getData();
        loadedDataRef.current = data;
        if (isHydratingRef.current) {
          // This fired from our own setData() above, not a user edit.
          return;
        }
        if (props.onChange) {
          props.onChange(event, editor);
        }
      }}
    />
  );
};
export default CustomEditor;